import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { UntilDestroy } from "@ngneat/until-destroy";
import { Transaction } from "@models/transaction";
import { SharePreviewComponent } from "@modules/social-trading/components/share-preview/share-preview.component";
import { ModalService } from "@core-services/modal.service";
import { IndexWalletService } from "@main-services/wallet/index_wallet.service";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { TRADE_LOCAKEY } from "@modules/social-trading/components/share-preview/share-preview.constants";

@UntilDestroy()
@Component({
	selector: "share-button",
	templateUrl: "./share-button.component.html",
	styleUrls: ["./share-button.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ShareButtonComponent {
	public constructor(
		protected modalService: ModalService,
		protected indexWalletService: IndexWalletService
	) {}

	@Input()
	public transaction: Transaction = null;

	@Input()
	public isFooterBtn: boolean = false;

	public shareTrade(): void {
		const isSwap = this.transaction.getTrade().getIsSwap() || this.transaction.isSwap();
		const assetId: string = this.getAssetId();
		const tradeType: string = isSwap ? "swap" : this.transaction.getType() || this.transaction.getTrade().getType();
		const transactionDate: Date = this.transaction.getTime().date();

		if (isSwap) {
			this.getRelatedAssetId().subscribe((relatedAssetId) => {
				this.openModalForSwap(assetId, relatedAssetId, tradeType, transactionDate);
			});
		} else {
			this.openModalForBuyOrSell(assetId, tradeType, transactionDate);
		}
	}

	private getAssetId(): string {
		return this.transaction.hasTrade()
			? this.transaction.getTrade().getCurrencyId()
			: this.transaction.getCurrencyId();
	}

	private getRelatedAssetId(): Observable<string> {
		if (this.transaction.isIndex()) {
			const walletId = this.transaction.getWalletId();
			return this.indexWalletService
				.getTradesForIndexTransaction(walletId, this.transaction._id)
				.pipe(map((trades) => trades[0]?.getRelatedSwapTrade()?.getCurrencyId()));
		}
		const relatedSwapTrade = this.transaction.getTrade().getRelatedSwapTrade();
		return of(relatedSwapTrade?.getCurrencyId());
	}

	private openModalForBuyOrSell(assetId, tradeType, transactionDate): void {
		this.modalService
			.create(SharePreviewComponent, { assetId, tradeType, transactionDate, locaKey: TRADE_LOCAKEY })
			.present();
	}

	private openModalForSwap(assetId, relatedAssetId, tradeType, transactionDate): void {
		const isOutgoing = this.transaction.isOutgoing();
		const { sourceAssetId, targetAssetId } = this.alignWithSwapDirection(isOutgoing, relatedAssetId, assetId);
		this.modalService
			.create(SharePreviewComponent, {
				sourceAssetId,
				assetId: targetAssetId,
				tradeType,
				transactionDate,
				locaKey: TRADE_LOCAKEY
			})
			.present();
	}

	private alignWithSwapDirection(
		isOutgoing: boolean,
		relatedAssetId: string,
		assetId: string
	): SwapSourceAndTargetAssets {
		if (isOutgoing) {
			return {
				sourceAssetId: relatedAssetId,
				targetAssetId: assetId
			};
		}
		return {
			sourceAssetId: assetId,
			targetAssetId: relatedAssetId
		};
	}
}

interface SwapSourceAndTargetAssets {
	sourceAssetId: string;
	targetAssetId: string;
}
